import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner, Alert, Modal, ProgressBar, Pagination, InputGroup } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';

const LedgerReports = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [topRetailersCollapsed, setTopRetailersCollapsed] = useState(true);
  const [paymentModeSummaryCollapsed, setPaymentModeSummaryCollapsed] = useState(true);

  // Refs to prevent duplicate calls
  const hasInitialized = useRef(false);
  
  const [filters, setFilters] = useState({
    date_from: new Date().toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    payment_mode: '',
    search_query: '',
    order_id: '',
    utr: '',
    page: 1,
    page_size: 50
  });

  // Fetch ledger reports
  const fetchReports = useCallback(async () => {
    if (!filters.date_from || !filters.date_to) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await distributorService.getLedgerReports(filters);
      
      if (response.success) {
        setReportData(response.data);
      } else {
        setError(response.error || 'Failed to fetch reports');
        toast.error(response.error || 'Failed to fetch reports');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to fetch reports');
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Handle rows per page change
  const handlePageSizeChange = (newPageSize) => {
    setFilters(prev => ({
      ...prev,
      page_size: parseInt(newPageSize),
      page: 1 // Reset to first page when page size changes
    }));
  };

  // Auto-fetch when page or page_size changes (only if we already have data)
  useEffect(() => {
    if (reportData && filters.date_from && filters.date_to) {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.page_size]);

  // Export to Excel
  const handleExport = async () => {
    if (!filters.date_from || !filters.date_to) {
      toast.error('Please select both start and end dates');
      return;
    }

    setExporting(true);
    
    try {
      const exportParams = {
        date_from: filters.date_from,
        date_to: filters.date_to,
        include_summary: false,
        payment_mode: filters.payment_mode || null,
        order_id: filters.order_id || null,
        utr: filters.utr || null
      };

      const response = await distributorService.exportLedgerReports(exportParams);
      
      if (response.success) {
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('Report exported successfully');
      } else {
        toast.error(response.error || 'Failed to export report');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      date_from: new Date().toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
      payment_mode: '',
      search_query: '',
      order_id: '',
      utr: '',
      page: 1,
      page_size: 50
    });
    setReportData(null);
    setError(null);
  };

  // View record details
  const viewDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const variants = {
      'COMPLETED': 'success',
      'PENDING': 'warning',
      'FAILED': 'danger',
      'CANCELLED': 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // Get transaction type badge
  const getTransactionTypeBadge = (type) => {
    return type === 'DEBIT' ? 
      <Badge bg="danger">{type}</Badge> : 
      <Badge bg="success">{type}</Badge>;
  };

  // Get source badge
  const getSourceBadge = (source) => {
    const variants = {
      'manual': 'primary',
      'fos': 'info',
      'auto': 'secondary'
    };
    return <Badge bg={variants[source] || 'secondary'}>{source}</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-chart-line me-2"></i>
          Ledger Reports
        </h5>
        <div className="d-flex gap-2">
          <Button 
            variant="success" 
            onClick={handleExport}
            disabled={!reportData || exporting}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Exporting...
              </>
            ) : (
              <>
                <i className="fas fa-file-excel me-2"></i>
                Export Excel
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="Order ID"
            value={filters.order_id}
            onChange={(e) => handleFilterChange('order_id', e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="UTR Number"
            value={filters.utr}
            onChange={(e) => handleFilterChange('utr', e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.payment_mode}
            onChange={(e) => handleFilterChange('payment_mode', e.target.value)}
          >
            <option value="">All Payment Modes</option>
            <option value="CASH">Cash</option>
            <option value="UPI">UPI</option>
            <option value="CREDIT">Credit</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
            placeholder="From Date"
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
            placeholder="To Date"
          />
        </Col>
        <Col md={2}>
          <Button 
            variant="primary" 
            onClick={fetchReports}
            disabled={loading}
            title="Generate Report"
            className="w-100"
          >
            {loading ? (
              <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem' }} />
            ) : (
              <i className="fas fa-search"></i>
            )}
          </Button>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Report Summary */}
      {reportData && reportData.summary && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-primary">{reportData.summary.total_summary.total_transactions}</h4>
                <p className="text-muted mb-0">Total Transactions</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-success">{formatCurrency(reportData.summary.total_summary.total_credit_amount)}</h4>
                <p className="text-muted mb-0">Total Credits</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-danger">{formatCurrency(reportData.summary.total_summary.total_debit_amount)}</h4>
                <p className="text-muted mb-0">Total Debits</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className={reportData.summary.total_summary.net_balance >= 0 ? 'text-success' : 'text-danger'}>
                  {formatCurrency(reportData.summary.total_summary.net_balance)}
                </h4>
                <p className="text-muted mb-0">Net Balance</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Payment Mode Summary */}
      {reportData && reportData.summary && reportData.summary.payment_mode_summary && (
        <div className="mb-4">
          <div 
            className="bg-light p-3 rounded-top border d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => setPaymentModeSummaryCollapsed(!paymentModeSummaryCollapsed)}
          >
            <h5 className="mb-0">
              Payment Mode Summary
              <Badge bg="secondary" className="ms-2">{reportData.summary.payment_mode_summary.length}</Badge>
            </h5>
            <i className={`fas fa-chevron-${paymentModeSummaryCollapsed ? 'down' : 'up'}`}></i>
          </div>
          {!paymentModeSummaryCollapsed && (
            <div className="table-responsive">
              <Table striped bordered hover className="modern-table mb-0">
                <thead>
                  <tr>
                    <th>Payment Mode</th>
                    <th>Transactions</th>
                    <th>Credit Amount</th>
                    <th>Debit Amount</th>
                    <th>Net Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.summary.payment_mode_summary.map((mode, index) => (
                    <tr key={index}>
                      <td><Badge bg="info">{mode.payment_mode}</Badge></td>
                      <td>{mode.total_transactions}</td>
                      <td className="text-success fw-bold">{formatCurrency(mode.total_credit_amount)}</td>
                      <td className="text-danger fw-bold">{formatCurrency(mode.total_debit_amount)}</td>
                      <td className={`fw-bold ${mode.net_balance >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatCurrency(mode.net_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Top Retailers */}
      {reportData && reportData.summary && reportData.summary.top_retailers && (
        <div className="mb-4">
          <div 
            className="bg-light p-3 rounded-top border d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => setTopRetailersCollapsed(!topRetailersCollapsed)}
          >
            <h5 className="mb-0">
              Top Retailers
              <Badge bg="info" className="ms-2">{reportData.summary.top_retailers.length}</Badge>
            </h5>
            <i className={`fas fa-chevron-${topRetailersCollapsed ? 'down' : 'up'}`}></i>
          </div>
          {!topRetailersCollapsed && (
            <div className="table-responsive">
              <Table striped bordered hover className="modern-table mb-0">
                <thead>
                  <tr>
                    <th>Retailer</th>
                    <th>Mobile</th>
                    <th>Transactions</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Net Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.summary.top_retailers.map((retailer, index) => (
                    <tr key={index}>
                      <td>
                        <div>
                          <strong>{retailer.retailer_name}</strong>
                          <br />
                          <small className="text-muted">ID: {retailer.retailer_id}</small>
                        </div>
                      </td>
                      <td>{retailer.retailer_mobile}</td>
                      <td>{retailer.transaction_count}</td>
                      <td className="text-success fw-bold">{formatCurrency(retailer.total_credit)}</td>
                      <td className="text-danger fw-bold">{formatCurrency(retailer.total_debit)}</td>
                      <td className={`fw-bold ${retailer.net_balance >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatCurrency(retailer.net_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Detailed Records */}
      {reportData && reportData.detailed_records && (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Txn Type</th>
                <th>Retailer</th>
                <th>Order ID</th>
                <th>Payment Mode</th>
                <th>UTR</th>
                <th>Transaction Date</th>
                <th>Remarks</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : reportData.detailed_records.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-4 text-muted">
                    No transaction records found
                  </td>
                </tr>
              ) : (
                reportData.detailed_records.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => viewDetails(record)}
                        title="View Details"
                      >
                        <i className="fas fa-eye"></i>
                      </Button>
                    </td>
                    <td>{getStatusBadge(record.status)}</td>
                    <td>
                      <div className={`fw-bold ${record.transaction_type === 'DEBIT' ? 'text-danger' : 'text-success'}`}>
                        {formatCurrency(record.amount)}
                      </div>
                    </td>
                    <td>{getTransactionTypeBadge(record.transaction_type)}</td>
                    <td>
                      <Badge bg="info">{record.txn_type || '-'}</Badge>
                    </td>
                    <td>
                      {record.retailer_name ? (
                        <div>
                          <strong>{record.retailer_name}</strong>
                          <br />
                          {/* <small className="text-muted">{record.retailer_mobile}</small>
                          <br/> */}
                          <small className="text-muted">{record.retailer_customer_id}</small>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">{record.order_id}</small>
                    </td>
                    <td><Badge bg="secondary">{record.payment_mode}</Badge></td>
                    <td>
                      {record.utr_number ? (
                        <div className="text-primary fw-bold">{record.utr_number}</div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                   

                    <td>
                      <div>{record.transaction_date}</div>
                      <small className="text-muted">{record.created_time}</small>
                    </td>

                     <td>
                      <small className="text-muted">{record.remarks}</small>
                    </td>
                    <td>{getSourceBadge(record.source)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {reportData && reportData.pagination && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <Form.Select
              value={filters.page_size}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </Form.Select>
          </div>
          <div>
            <small className="text-muted">
              Showing {((reportData.pagination.current_page - 1) * reportData.pagination.page_size) + 1} to{' '}
              {Math.min(reportData.pagination.current_page * reportData.pagination.page_size, reportData.pagination.total_records)} of{' '}
              {reportData.pagination.total_records} entries
            </small>
          </div>
          {reportData.pagination.total_pages > 1 && (
            <Pagination>
              <Pagination.First 
                onClick={() => handlePageChange(1)} 
                disabled={!reportData.pagination.has_previous} 
              />
              <Pagination.Prev 
                onClick={() => handlePageChange(reportData.pagination.current_page - 1)} 
                disabled={!reportData.pagination.has_previous} 
              />
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;
                const totalPages = reportData.pagination.total_pages;
                const currentPage = reportData.pagination.current_page;
                
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                
                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Pagination.Item
                      key={i}
                      active={i === currentPage}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Pagination.Item>
                  );
                }
                
                return pages;
              })()}
              <Pagination.Next 
                onClick={() => handlePageChange(reportData.pagination.current_page + 1)} 
                disabled={!reportData.pagination.has_next} 
              />
              <Pagination.Last 
                onClick={() => handlePageChange(reportData.pagination.total_pages)} 
                disabled={!reportData.pagination.has_next} 
              />
            </Pagination>
          )}
        </div>
      )}

      {/* No Data Message */}
      {!loading && !reportData && (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Txn Type</th>
                <th>Retailer</th>
                <th>Order ID</th>
                <th>Payment Mode</th>
                <th>UTR</th>
                <th>Transaction Date</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan="12" className="text-center py-5">
                  <div>
                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Report Generated</h5>
                    <p className="text-muted">Select date range and click "Generate Report" to view ledger data</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      )}

      {/* Record Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transaction Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Transaction ID:</strong>
                  <div>{selectedRecord.id}</div>
                </div>
                <div className="mb-3">
                  <strong>Order ID:</strong>
                  <div className="font-monospace">{selectedRecord.order_id}</div>
                </div>
                <div className="mb-3">
                  <strong>Retailer:</strong>
                  <div>{selectedRecord.retailer_name}</div>
                  <small className="text-muted">{selectedRecord.retailer_mobile}</small>
                </div>
                <div className="mb-3">
                  <strong>Amount:</strong>
                  <div className={selectedRecord.transaction_type === 'DEBIT' ? 'text-danger' : 'text-success'}>
                    {formatCurrency(selectedRecord.amount)}
                  </div>
                </div>
                <div className="mb-3">
                  <strong>Transaction Type:</strong>
                  <div>{getTransactionTypeBadge(selectedRecord.transaction_type)} ({selectedRecord.txn_type})</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Date & Time:</strong>
                  <div>{selectedRecord.transaction_date} {selectedRecord.created_time}</div>
                </div>
                <div className="mb-3">
                  <strong>Payment Mode:</strong>
                  <div><Badge bg="secondary">{selectedRecord.payment_mode}</Badge></div>
                </div>
                <div className="mb-3">
                  <strong>Status:</strong>
                  <div>{getStatusBadge(selectedRecord.status)}</div>
                </div>
                <div className="mb-3">
                  <strong>Source:</strong>
                  <div>{getSourceBadge(selectedRecord.source)}</div>
                </div>
                <div className="mb-3">
                  <strong>Collected By:</strong>
                  <div>{selectedRecord.collected_by_mobile}</div>
                </div>
                {selectedRecord.utr_number && (
                  <div className="mb-3">
                    <strong>UTR Number:</strong>
                    <div className="font-monospace">{selectedRecord.utr_number}</div>
                  </div>
                )}
                {selectedRecord.remarks && (
                  <div className="mb-3">
                    <strong>Remarks:</strong>
                    <div>{selectedRecord.remarks}</div>
                  </div>
                )}
              </Col>
              <Col md={12}>
                <hr />
                <div className="row">
                  <div className="col-md-4">
                    <strong>Opening Balance:</strong>
                    <div>{formatCurrency(selectedRecord.opening_balance)}</div>
                  </div>
                  <div className="col-md-4">
                    <strong>Transaction Amount:</strong>
                    <div className={selectedRecord.transaction_type === 'DEBIT' ? 'text-danger' : 'text-success'}>
                      {selectedRecord.transaction_type === 'DEBIT' ? '-' : '+'}{formatCurrency(selectedRecord.amount)}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <strong>Closing Balance:</strong>
                    <div>{formatCurrency(selectedRecord.closing_balance)}</div>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LedgerReports;